import { TableCell } from '@/components/Datatable/TableCell';
import { IDatatableData } from '@/type/listing.types';
import { FieldValues } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { AddressDisplay } from '@/components/AddressDisplay';
import { Checkbox } from '@/components/Controls/Checkbox';
import { TableHeaderActions } from '@/components/Custom/Datatable/TableHeaderActions';
import { useSelectedTableItems } from '@/hooks/selected-table-items.hooks';
import { TableMenu } from '@/components/Menu/TableMenu';
import { SubMenuItem } from '@/components/Menu/SubMenuItem';
import { CellContentWithRedirect } from '@/components/Custom/Datatable/CellContentWithRedirect';
import { Avatar } from '@/components/Avatar';
import { StatusTag } from '@/components/Tag/StatusTag';

export const CompanyTableData = <TSchema extends FieldValues>({
  t,
  data,
  pendingDeactivate,
  onDeactivate,
}: IDatatableData<TSchema>) => {
  const navigate = useNavigate();

  const {
    numberOfItemsText,
    selectedTableData,
    onSelectedItem,
    handleDeactivate,
  } = useSelectedTableItems({
    t,
    data,
    onDeactivate,
  });

  if (!data || data.length === 0) {
    return <></>;
  }

  return (
    <>
      {selectedTableData?.length > 0 && (
        <TableHeaderActions
          colSpan={8}
          text={numberOfItemsText}
          disableDeactivate={selectedTableData?.every((item) => !item?.active)}
          disableDownload
          onDeactivate={handleDeactivate}
          onDownload={() => {}}
          onCreate={() => navigate('/company/create')}
        />
      )}
      {data.map((entry: TSchema) => {
        return (
          <tr
            key={entry?.id}
            className={twMerge('', !entry?.active && 'tr-deactivated')}
          >
            <TableCell
              tdClassName="sticky-select-column"
              content={
                <div className="flex justify-center items-center">
                  <Checkbox
                    checked={Boolean(
                      selectedTableData?.find((item) => item?.id === entry?.id),
                    )}
                    onChange={() => onSelectedItem(entry?.id)}
                  />
                </div>
              }
            />
            <TableCell content={entry?.name} />
            <TableCell
              content={
                <CellContentWithRedirect
                  text={entry?.branches?.length}
                  disabled={!entry?.branches?.length}
                  onClick={() => navigate(`/branches/company/${entry?.id}`)}
                />
              }
            />
            <TableCell
              content={
                entry?.group && (
                  <div className="flex items-center gap-2">
                    <Avatar
                      imageUrl={entry?.group?.logo}
                      containerClassName="w-[33px] h-[33px] border-0 shadow-0 rounded-lg [&>.profile-image-wrapper]:rounded-md"
                    />
                    <div className="flex items-center text-[13.5px] leading-[17px]">
                      {entry?.group?.name}
                    </div>
                  </div>
                )
              }
            />
            <TableCell
              content={
                <div className="flex flex-wrap justify-center gap-2">
                  {entry?.group?.isDistributor && (
                    <StatusTag
                      text={t('common:distributor')}
                      className="min-w-[90px] bg-secondary-light border-gray-DBDFE9 text-gray-78829D text-xs dark-mode:border-gray-300 dark-mode:bg-gray-100 dark-mode:text-gray-700 dark-mode:font-normal"
                    />
                  )}
                  {entry?.group?.isBranch && (
                    <StatusTag
                      text={t('common:branch')}
                      className="min-w-[90px] bg-secondary-light border-gray-DBDFE9 text-gray-78829D text-xs dark-mode:border-gray-300 dark-mode:bg-gray-100 dark-mode:text-gray-700 dark-mode:font-normal"
                    />
                  )}
                </div>
              }
            />
            <TableCell
              content={
                <>
                  {AddressDisplay({
                    addressLine: entry?.addressLine1,
                    postalCode: entry?.postalCode,
                    city: entry?.city,
                    country: entry?.country,
                  })}
                </>
              }
            />
            <TableCell
              tdClassName="text-center"
              content={
                <StatusTag
                  text={`${
                    entry?.group?.isWhiteLabel
                      ? t('common:active')
                      : t('common:inactive')
                  }`}
                  className={twMerge(
                    'pt-[6px] pb-[5px] text-[10.1px] font-semibold [word-spacing:1.9px] leading-snug uppercase',
                    entry?.group?.isWhiteLabel &&
                      'bg-green-EAFFF2 border-green-C9F4D2 text-green-11BFA4',
                    !entry?.group?.isWhiteLabel &&
                      'bg-red-FFEEF4 border-red-FFCAD6 text-red-FF3680',
                  )}
                />
              }
            />
            <TableCell
              tdClassName="sticky-action-column"
              content={
                <TableMenu>
                  <SubMenuItem
                    text={t('common:edit')}
                    leftIconClassName="ki-notepad-edit !text-[16px]"
                    to={`/company/edit/${entry?.id}`}
                  />
                  <SubMenuItem
                    text={t('common:deactivate')}
                    disabled={!entry?.active || pendingDeactivate}
                    leftIconClassName="ki-trash !text-[19px]"
                    textClassName="line-clamp-1"
                    onClick={() => onDeactivate([entry])}
                  />
                </TableMenu>
              }
            />
          </tr>
        );
      })}
    </>
  );
};
